import { cn } from '@/lib/utils';
import { HYPOTHESIS_STATE_LABELS, setHypothesisState } from '@/lib/company-qualification';
import {
  type CompanyQualificationSheet,
  type HypothesisState,
  HYPOTHESIS_STATES,
} from '@shared/types/company-qualification';

const TONES: Record<HypothesisState, string> = {
  open: 'bg-amber-50 text-amber-800',
  confirmed: 'bg-emerald-50 text-emerald-800',
  refuted: 'bg-stone-100 text-stone-600',
};

/**
 * The analyst's working hypotheses: its best answer from the start, tested turn
 * after turn. With `onChange`, a manager decides one by hand.
 */
export const WorkingHypotheses = ({ sheet, onChange, className, heading = true }: {
  sheet: CompanyQualificationSheet;
  onChange?: (sheet: CompanyQualificationSheet) => void;
  className?: string;
  heading?: boolean;
}) => {
  if (sheet.hypotheses.length === 0) return null;
  return (
    <div className={className}>
      {heading && <h4 className="text-sm font-semibold uppercase tracking-wide text-domino-inkSoft">Hypothèses de travail</h4>}
      <ul className={cn('space-y-2.5', heading && 'mt-2')}>
        {sheet.hypotheses.map((hypothesis) => (
          <li key={hypothesis.id} className="flex items-start gap-3">
            {onChange ? (
              <select
                aria-label={`État de l’hypothèse : ${hypothesis.text}`}
                value={hypothesis.state}
                onChange={(event) => onChange(setHypothesisState(sheet, hypothesis.id, event.target.value as HypothesisState))}
                className={cn('mt-0.5 shrink-0 rounded-full border-0 px-2 py-0.5 text-xs', TONES[hypothesis.state])}
              >
                {HYPOTHESIS_STATES.map((state) => <option key={state} value={state}>{HYPOTHESIS_STATE_LABELS[state]}</option>)}
              </select>
            ) : (
              <span className={cn('mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-xs', TONES[hypothesis.state])}>{HYPOTHESIS_STATE_LABELS[hypothesis.state]}</span>
            )}
            <div className="min-w-0 text-sm leading-6">
              <p className={cn('text-domino-ink', hypothesis.state === 'refuted' && 'text-domino-inkSoft line-through decoration-stone-300')}>{hypothesis.text}</p>
              <p className="text-xs text-domino-inkSoft" title={hypothesis.evidence?.excerpt}>
                {hypothesis.state === 'open' ? `À vérifier : ${hypothesis.test}` : hypothesis.evidence?.label}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
